// =============================================================================
// features/meu-whatsapp/AiAutoreplyToggle.tsx — Trava de IA por caixa (F43-S13).
//
// Regra do produto (Rogério, doc planejamento-inbox-vendedores-waha.md §3.5):
// quem liga e desliga é SOMENTE o `admin` — nem gestor, nem o próprio
// vendedor. O componente só é MONTADO por quem já tem
// `channel.ai_autoreply:manage` (gate no componente pai, `MeuWhatsAppInboxPage`)
// — esconder aqui é cosmético, o backend (F43-S09/S15) é quem barra de verdade.
//
// Texto fala o EFEITO, não o mecanismo: "Responder automaticamente quando
// ninguém tiver assumido a conversa" — nunca "ativar IA".
//
// DS §9.9: pill 44×24, track --surface-muted (desligado) / --brand-verde
// (ligado), thumb 20×20 branca com sombra, inset shadow no track.
// =============================================================================

import * as React from 'react';

import { useChannelAiAutoreply, useSetChannelAiAutoreply } from './useChannelAiAutoreply';

// ---------------------------------------------------------------------------
// Switch — primitivo local (components/ui/** fora de files_allowed deste slot)
// ---------------------------------------------------------------------------

interface SwitchProps {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
  readonly labelledBy: string;
}

function Switch({ checked, disabled, onToggle, labelledBy }: SwitchProps): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={onToggle}
      className="relative inline-flex flex-shrink-0 items-center rounded-pill transition-colors duration-fast ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-azul/30 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        width: 44,
        height: 24,
        background: checked ? 'var(--brand-verde)' : 'var(--surface-muted)',
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15)',
      }}
    >
      <span
        aria-hidden="true"
        className="inline-block rounded-full bg-white transition-transform duration-fast ease-out"
        style={{
          width: 20,
          height: 20,
          transform: checked ? 'translateX(22px)' : 'translateX(2px)',
          boxShadow: 'var(--elev-1)',
        }}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// AiAutoreplyToggle
// ---------------------------------------------------------------------------

export interface AiAutoreplyToggleProps {
  readonly channelId: string;
}

/**
 * AiAutoreplyToggle — liga/desliga a resposta automática da IA nesta caixa.
 *
 * Só deve ser renderizado por quem tem `channel.ai_autoreply:manage` — o
 * chamador (`MeuWhatsAppInboxPage`) faz esse gate via `useAuth().hasPermission`.
 */
export function AiAutoreplyToggle({ channelId }: AiAutoreplyToggleProps): React.JSX.Element | null {
  const { data, isLoading } = useChannelAiAutoreply(channelId, { enabled: true });
  const setAutoreply = useSetChannelAiAutoreply(channelId);

  if (isLoading || data === undefined) {
    return (
      <div
        className="rounded-sm animate-pulse"
        style={{ width: 240, height: 24, background: 'var(--surface-muted)' }}
        role="status"
        aria-label="Carregando preferência de resposta automática"
      />
    );
  }

  // Canal não encontrado na lista (raro — canal recém-criado, cache defasado):
  // degrada em silêncio, sem oferecer um controle sobre dado que não existe.
  if (data === null) return null;

  const enabled = data.ai_autoreply_enabled;

  return (
    <div className="flex items-start gap-3">
      <Switch
        checked={enabled}
        disabled={setAutoreply.isPending}
        onToggle={() => setAutoreply.mutate(!enabled)}
        labelledBy="ai-autoreply-label"
      />
      <div className="flex flex-col gap-0.5">
        <span
          id="ai-autoreply-label"
          className="font-sans font-semibold"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}
        >
          Resposta automática nesta caixa
        </span>
        <span className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>
          Responder automaticamente quando ninguém tiver assumido a conversa
        </span>
      </div>
    </div>
  );
}
